import type {
  AxisLabelsFormatterContextObject,
  AxisTitleAlignValue,
  AxisTypeValue,
  BoxPlotPoint,
  Chart,
  CSSObject,
  DashStyleValue,
  Options as HighchartOptions,
  Options,
  PlotLineOrBand,
  SeriesMapDataOptions,
  SVGRenderer,
  TooltipFormatterContextObject,
  XAxisOptions,
} from 'highcharts';
import { correctFloat, dateFormat, numberFormat } from 'highcharts';
import { compact, get, isNil, maxBy, merge, min, minBy, noop, round } from 'lodash';
import moment from 'moment';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import type { FrequencyEnum } from 'venn-api';
import type { Schemes, Theme, Typography, VennColors } from 'venn-ui-kit';
import {
  ColorUtils,
  getTextThemeProvider,
  HighchartZIndex,
  Icon,
  REPORT_LAB_FONT_BODY,
  Tooltip,
  TooltipPosition,
  getHelpCenterLinks,
} from 'venn-ui-kit';
import {
  type AnyDuringEslintMigration,
  assertNotNil,
  Dates,
  formatAllocation,
  Numbers,
  type TabularDataTypeEnum,
} from 'venn-utils';
import type { ScatterSeries } from '../../studio-blocks';
import type {
  ScatterAxisConfig,
  ScatterAxisRange,
  ScatterAxisRenderedExtremes,
  ScatterAxisStaticHints,
  ScatterAxisStaticMetadata,
} from 'venn-state';
import type { AxisFormat } from './scatter-axis-format';
import { axisFormatFromDataType, isPercent } from './scatter-axis-format';
import { displayToChart, dropCrowdedTicks, forceEndTicks, resolveScatterXAxisLabels } from './scatter-axis-units';
import type { LabelBox } from './scatter-axis-units';
import { WORLD_MAP_COUNTRIES } from '../../studio-blocks/components/charts/map-chart/mapTopology';
import type { VennSeriesEntry, VennSeriesPieDataOptions, VennSeriesPiePoint } from '../../studio-blocks/types';
import { default as HighchartsUtils } from '../../utils/highcharts';

import type {
  FullPeriodValue,
  HighchartChartType,
  PartialHighchartSerieData,
  SerieValue,
  UnitFormat,
} from '../analysis-charts/types';

export const getTooltipBackgroundColor = (colors: VennColors) => ColorUtils.hex2rgba(colors.Black, 0.9);

export const calculateLegendPadding = (legendFontSize: number) => 5 + 0.4 * legendFontSize;

export interface VennLineChartData {
  start: number;
  end: number;
  visibleVenncastStart?: number;
  visibleVenncastEnd?: number;
  series?: PartialHighchartSerieData[];
  estimated?: boolean;
  horizontalLine?: number;
  persistentBottomBar?: boolean;
}

const isEstimatedSeriesName = (seriesName: string) => seriesName === 'Estimated' || seriesName.includes('- Estimated');
const isErrorRangeSeriesName = (seriesName: string) =>
  seriesName === 'Error range' || seriesName.includes('- Error range');

const getTooltipItem = (
  point: TooltipFormatterContextObject,
  series: TooltipFormatterContextObject['series'],
  range: { y: number } | undefined,
  yAxisUnitFormat: UnitFormat = 'percent',
  currencySymbol?: string,
  customNumberFormater?: (value?: number | string | null, precision?: number) => string,
) => {
  const format = customNumberFormater ?? Numbers.safeFormatNumber;
  if (isEstimatedSeriesName(series.name) && !!range) {
    const errorDif = Math.abs(point.y - range.y);
    return `
      <br/>
      <span style='color:${point.color}'>\u25CF</span> ${series.name}
      : <b>${format(toPercentage(point.y))}%</b>
      <span>+/-${format(toPercentage(errorDif))}%</span>
    `;
  }
  if (yAxisUnitFormat === 'allocation') {
    return `<br/><span style='color:${point.color}'>\u25CF</span> ${series.name}
    : <b>${formatAllocation(point.y, true, false, undefined, false, 1)}</b>`;
  }
  if (currencySymbol) {
    return `<br/><span style='color:${point.color}'>\u25CF</span> ${series.name}
    : <b>${currencySymbol + format(point.y)}</b>`;
  }
  if (yAxisUnitFormat === 'ratio') {
    return `<br/><span style='color:${point.color}'>\u25CF</span> ${series.name}
    : <b>${format(point.y)}</b>`;
  }

  const isPercentageStackedAreaChart =
    series.type === 'area' && series.chart.options.plotOptions?.area?.stacking === 'percent';

  const value = isPercentageStackedAreaChart ? point.percentage : toPercentage(point.y);

  return `<br/><span style='color:${point.color}'>\u25CF</span> ${series.name}
  : <b>${format(value)}%</b>`;
};

export const getCustomTooltip = (
  yAxisUnitFormat: UnitFormat = 'percent',
  frequency: FrequencyEnum = 'MONTHLY',
  colors: VennColors,
  currencySymbol: string | undefined,
  customNumberFormater?: (value?: number | string | null, precision?: number) => string,
) => ({
  backgroundColor: getTooltipBackgroundColor(colors),
  borderRadius: 10,
  borderWidth: 0,
  valueDecimals: 2,
  shared: true,
  outside: false,
  useHTML: true,
  style: {
    color: colors.White,
    fontSize: REPORT_LAB_FONT_BODY,
  },
  formatter(this: TooltipFormatterContextObject) {
    if (!this || !this.points) {
      return false;
    }
    const estimatedRange = this.points.find((point: unknown) =>
      isErrorRangeSeriesName(get(point, 'series.name') ?? ''),
    );
    const tooltipFormat =
      frequency === 'DAILY' || estimatedRange ? 'D MMM YYYY' : frequency === 'QUARTERLY' ? '[Q]Q YYYY' : 'MMMM YYYY';

    let tooltipStr =
      frequency === 'QUARTERLY' && typeof this.x === 'string'
        ? `<div style="margin-bottom: -10px">${this.x}</div>`
        : `<div style="margin-bottom: -10px">${moment.utc(this.x).format(tooltipFormat)}</div>`;

    const pointsForTooltip = this.points
      .filter((point) => !isErrorRangeSeriesName(get(point, 'series.name')))
      .filter((point) => !point.series.userOptions?.hideFromSharedTooltip);
    return (tooltipStr += pointsForTooltip
      .map((point) => {
        let toolTipItem = getTooltipItem(
          point,
          point.series,
          estimatedRange,
          yAxisUnitFormat,
          currencySymbol,
          customNumberFormater,
        );

        if (pointsForTooltip.length > 1 && point.series.chart.hoverSeries === point.series) {
          toolTipItem = `<span style="font-weight:700; background-color:${ColorUtils.hex2rgba(colors.Primary.Main, 0.2)}">${toolTipItem}</span>`;
        }
        return `<span style="line-height: 1.3">${toolTipItem}</span>`;
      })
      .join(''));
  },
});

const numberFormatter = (value?: number) => {
  if (value === undefined) return '--';
  return numberFormat(value, 2);
};
const percentFormatter = (value?: number) => {
  if (value === undefined) return '--';
  return `${numberFormat(toPercentage(value), 1)}%`;
};

export const getPeerGroupSubjectTooltip = (
  colors: VennColors,
  yAxisUnitFormat: 'percent' | 'ratio',
  chartType: 'box' | 'stacked',
) => {
  return {
    backgroundColor: getTooltipBackgroundColor(colors),
    borderRadius: 10,
    borderWidth: 0,
    valueDecimals: 2,
    split: true,
    style: {
      color: colors.White,
    },
    formatter() {
      return '';
    },
    pointFormatter(this: Highcharts.Point) {
      const formatFn = yAxisUnitFormat === 'ratio' ? numberFormatter : percentFormatter;
      const tooltipStr: string = `${this.category}${chartType === 'box' ? '' : ' Percentile'}: <b>${formatFn(this.y)}</b>`;
      return tooltipStr;
    },
  };
};

export const getPeerGroupTooltip = (colors: VennColors, yAxisUnitFormat: 'percent' | 'ratio') => {
  return {
    backgroundColor: getTooltipBackgroundColor(colors),
    borderRadius: 10,
    borderWidth: 0,
    valueDecimals: 2,
    split: true,
    style: {
      color: colors.White,
    },
    formatter() {
      return '';
    },
    pointFormatter(this: TooltipFormatterContextObject): string {
      const formatFn = yAxisUnitFormat === 'ratio' ? numberFormatter : percentFormatter;
      const x = this.x;
      const currentData = this.series.data.find((data) => data.x === x);
      const boxplotValues = currentData?.options;
      return `5th Percentile: ${formatFn(boxplotValues?.low)}<br>25th Percentile: ${formatFn(boxplotValues?.q1)}<br>Median: ${formatFn(boxplotValues?.median)}<br>75th Percentile: ${formatFn(boxplotValues?.q3)}<br>95th Percentile: ${formatFn(boxplotValues?.high)}<br>`;
    },
  };
};

/** Default for most charts, except maps. */
export const getDefaultChartConfig = (
  yAxisUnitFormat: UnitFormat = 'percent',
  frequency: FrequencyEnum = 'MONTHLY',
  print: boolean,
  typography: Typography,
  colors: VennColors,
  schemes: Schemes,
  axisFontSize: number,
  currencySymbol?: string,
) => {
  return {
    credits: {
      enabled: false,
    },
    chart: {
      backgroundColor: 'transparent',
      marginRight: 0,
      marginTop: 0,
      spacingBottom: 4,
      style: {
        fontSize: print ? REPORT_LAB_FONT_BODY : '14px',
        fontFamily: typography.fontFamily,
        overflow: 'visible',
      },
    },
    title: {
      text: '',
    },
    legend: {
      enabled: false,
    },
    tooltip: getCustomTooltip(yAxisUnitFormat, frequency, colors, currencySymbol),
    xAxis: {
      type: 'datetime' as AxisTypeValue,
      gridLineWidth: 1,
      gridLineColor: schemes.LineChartColors.lineColor,
      tickWidth: 0,
      lineColor: schemes.LineChartColors.lineColor,
      tickPixelInterval: 120,
      crosshair: {
        color: colors.GreyScale.Grey50,
      },
      labels: {
        style: {
          fontSize: `${axisFontSize}pt`,
          color: colors.GreyScale.Grey90,
        },
        formatter(this: AxisLabelsFormatterContextObject) {
          const min: number = this.chart?.xAxis?.[0]?.dataMin || 0;
          const max: number = this.chart?.xAxis?.[0]?.dataMax || 0;
          const range = moment.duration(max - min).asYears();

          if (range > 1 && frequency === 'DAILY') {
            // X-axis labels use month-year format, and the returns are daily. We're okay with the fact that
            // this means that the label for "Mar '17" is aligned with the return point for "28 February 2017"
            return dateFormat(Dates.CHART_DATE_FORMATS.YEAR_MONTH, moment.utc(this.value).valueOf());
          }
          const format =
            frequency === 'DAILY' ? Dates.CHART_DATE_FORMATS.MONTH_DAY : Dates.CHART_DATE_FORMATS.YEAR_MONTH;

          return dateFormat(format, HighchartsUtils.alignDatesForLowFrequencies(this.value).valueOf());
        },
      },
    },
    yAxis: {
      gridLineColor: schemes.LineChartColors.lineColor,
      gridLineWidth: 1,
      tickWidth: 1,
      tickColor: schemes.LineChartColors.lineColor,
      tickLength: 0,
      lineWidth: 1,
      lineColor: schemes.LineChartColors.lineColor,
      showLastLabel: false,
      labels: {
        style: {
          fontFamily: typography.fontFamily,
          fontSize: `${axisFontSize}pt`,
          whiteSpace: 'nowrap',
        },
        useHTML: true,
        formatter(this: AxisLabelsFormatterContextObject) {
          switch (yAxisUnitFormat) {
            case 'ratio':
              return numberFormat(Number(this.value), 2);
            case 'allocation':
              return formatAllocation(Number(this.value), true, false, undefined, false, 1);
            case 'percent':
            default:
              return `${numberFormat(toPercentage(Number(this.value)), 1)}%`;
          }
        },
      },
      title: {
        text: '',
        y: 20,
        x: 20,
        align: 'low' as AxisTitleAlignValue,
        rotation: 0,
        offset: 0,
      },
    },
    plotOptions: {
      series: {
        animation: false,
        events: {
          legendItemClick: () => false,
        },
        marker: {
          enabled: false,
          states: {
            hover: {
              radius: 4,
            },
          },
        },
      },
    },
  };
};

export const getMinValue = (series: PartialHighchartSerieData[] | undefined, horizontalLine: number | undefined) => {
  if (!series || series.length === 0 || horizontalLine === undefined) {
    return undefined;
  }
  let minValue = horizontalLine;
  // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
  series.forEach((serie) => serie.data.forEach((item) => (minValue = Math.min(item[1], minValue))));

  return minValue;
};

export const getMaxValue = (series: PartialHighchartSerieData[] | undefined, horizontalLine: number | undefined) => {
  if (!series || series.length === 0 || horizontalLine === undefined) {
    return undefined;
  }
  let maxValue = horizontalLine;
  // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
  series.forEach((serie) => serie.data.forEach((item) => (maxValue = Math.max(item[1], maxValue))));
  return maxValue;
};

type FullPeriodValueWithColor = FullPeriodValue & { color: string };
export const createVennLineChartConfig = (
  series: SerieValue[],
  theme: Theme,
  axisFontSize: number,
  yAxisUnitFormat: 'percent' | 'ratio' | 'allocation' = 'percent',
  frequency: FrequencyEnum = 'MONTHLY',
  minStartDate: number | undefined,
  print = false,
  showDrawdownLine = false,
  currencySymbol?: string,
): HighchartOptions => {
  const horizontalLines: FullPeriodValueWithColor[] = compact(
    series.map((serie: SerieValue) =>
      serie.fullPeriodValueLine
        ? {
            ...serie.fullPeriodValueLine,
            color: serie.color,
          }
        : undefined,
    ),
  );

  const data = compact(series.map((s) => s.data));

  const minByHorizontalLines = minBy(horizontalLines, (hl: FullPeriodValue) => hl.value);
  const maxByHorizontalLines = maxBy(horizontalLines, (hl: FullPeriodValue) => hl.value);

  const min = getMinValue(data, minByHorizontalLines ? minByHorizontalLines.value : undefined);
  const max = getMaxValue(data, maxByHorizontalLines ? maxByHorizontalLines.value : undefined);

  const yAxisConstraint =
    horizontalLines.length > 0
      ? {
          min,
          max,
        }
      : {};
  const xAxisConstraint = minStartDate ? { min: minStartDate } : {};

  const config = getDefaultChartConfig(
    yAxisUnitFormat,
    frequency,
    print,
    theme.Typography,
    theme.Colors,
    theme.Schemes,
    axisFontSize,
    currencySymbol,
  );

  const additionalDrawdownYaxis = showDrawdownLine
    ? {
        max: 0.01,
        minRange: 0.03,
        plotLines: [
          {
            color: theme.Colors.GreyScale.Grey90,
            value: 0.0,
            width: 2,
            dashStyle: 'ShortDash' as DashStyleValue,
          },
        ],
      }
    : {};

  return {
    ...config,
    chart: {
      ...config.chart,
      height: print ? 200 : undefined,
    },
    series: data.map((serie: PartialHighchartSerieData) => ({
      ...serie,
      type: serie.type as AnyDuringEslintMigration,
    })),
    xAxis: {
      ...config.xAxis,
      ...xAxisConstraint,
      id: 'xaxis',
      labels: {
        ...config.xAxis.labels,
      },
    },
    yAxis: {
      ...config.yAxis,
      ...yAxisConstraint,
      plotLines: horizontalLines.map((hl: FullPeriodValueWithColor) => ({
        color: hl.color,
        width: 1,
        value: hl.value,
        dashStyle: 'ShortDash' as DashStyleValue,
        zIndex: HighchartZIndex.Serie.Front,
      })),
      ...additionalDrawdownYaxis,
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      ...config.plotOptions,
      area: { fillOpacity: 0.2 },
    },
  };
};

export const createVennMapChartConfig = (
  series: SeriesMapDataOptions[],
  colors: VennColors,
  legendFontSize: number,
): HighchartOptions => {
  return {
    chart: {
      backgroundColor: 'transparent',
      spacingTop: 0,
      spacingRight: 0,
      spacingBottom: 2,
      spacingLeft: 0,
    },
    credits: {
      enabled: false,
    },
    mapNavigation: {
      enabled: false,
    },
    title: {
      text: '',
    },
    colorAxis: {
      minColor: colors.DivergingColor.B1,
      maxColor: colors.DivergingColor.B5,
      min: 0,
      max: 1,
      labels: {
        formatter() {
          return Numbers.safeFormatPercentage(this.value, 0);
        },
        style: {
          textOverflow: 'none',
          fontSize: `${legendFontSize}pt`,
        },
      },
    },
    legend: {
      padding: calculateLegendPadding(legendFontSize),
      margin: 0,
    },
    series: [
      {
        type: 'map',
        mapData: WORLD_MAP_COUNTRIES,
        name: 'Geography exposure',
        data: series,
        keys: ['hc-key', 'value', 'region-name'],
        tooltip: {
          pointFormatter() {
            // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
            return `${assertNotNil(this['region-name'])}: ${Numbers.safeFormatPercentage(this.value)}`;
          },
        },
      },
    ],
  };
};

const getSeriesConfigs = (seriesCount: number) => {
  if (seriesCount <= 2) {
    return [{ innerSize: '80%' }, { innerSize: '72%' }].slice(0, seriesCount).reverse();
  }

  return [
    { innerSize: '80%' },
    { innerSize: '64%' },
    { innerSize: '54.4%' },
    { innerSize: '46.24%' },
    { innerSize: '39.3%' },
    { innerSize: '33.4%' },
    { innerSize: '28.39%' },
    { innerSize: '24.13%' },
    { innerSize: '20.51%' },
    { innerSize: '17.43%' },
    { innerSize: '14.8%' },
    { innerSize: '12.6%' },
    { innerSize: '10.7%' },
    { innerSize: '9.1%' },
  ]
    .slice(0, seriesCount)
    .reverse();
};

export const createVennPieChartConfig = (
  seriesEntries: VennSeriesEntry[] | undefined = [],
  theme: Theme,
  yAxisUnitFormat: 'percent' | 'ratio' = 'percent',
  height: number | undefined,
  legendFontSize: number,
  print = false,
  customLegend = false,
  allowNegativeValues = false,
  vdparFundIds?: Set<string>,
  vdparSymbol?: string,
): HighchartOptions => {
  const config = getDefaultChartConfig(
    yAxisUnitFormat,
    'MONTHLY',
    print,
    theme.Typography,
    theme.Colors,
    theme.Schemes,
    // We don't use an axis for pie chart
    0,
    undefined,
  );

  const seriesConfigs = getSeriesConfigs(seriesEntries.length);

  return {
    ...config,
    xAxis: undefined,
    yAxis: undefined,
    chart: {
      ...config.chart,
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
      marginBottom: 0,
      height,
      type: 'pie',
      animation: false,
      reflow: true,
    },
    series: seriesEntries.map((seriesEntry, index) => ({
      type: 'pie',
      ...seriesConfigs[index],
      ...seriesEntry,
    })),

    tooltip: {
      useHTML: true,
      formatter(this: TooltipFormatterContextObject) {
        const point = this.point as VennSeriesPiePoint;
        const pointOptions = point.options as VennSeriesPieDataOptions;
        const pointName = point.name ?? '';
        const fundId = pointOptions?.fundId;
        const footnote = fundId && vdparFundIds?.has(fundId) && vdparSymbol ? `<sup>${vdparSymbol}</sup>` : '';

        if (allowNegativeValues) {
          return `<span style="font-size: 12px; font-weight: normal;">${pointName}${footnote}</span>`;
        }

        const percentage =
          point.aggregatedPercentage !== undefined ? point.aggregatedPercentage * 100.0 : point.percentage;
        const nameAndValue = `<span style="font-size: 12px; font-weight: normal;">${pointName}${footnote}: <b>${Numbers.safeFormatNumber(percentage, 1)}%</b></span>`;

        const isOuterRing = (point.series?.index ?? 0) > 0;
        const containingStrategy = pointOptions?.parentLabel;
        if (isOuterRing && containingStrategy) {
          return `<div style="display: flex; flex-direction: column; gap: 2px;"><span style="font-size: 10px; text-transform: uppercase;">${containingStrategy}</span>${nameAndValue}</div>`;
        }

        return nameAndValue;
      },
    },
    legend: {
      enabled: !customLegend,
      verticalAlign: 'bottom',
      labelFormatter(this: VennSeriesPieDataOptions) {
        return this.legend ?? '';
      },
      itemStyle: {
        fontSize: `${legendFontSize}pt`,
      },
      padding: legendFontSize ? calculateLegendPadding(legendFontSize) : 0,
      margin: 0,
    },
    plotOptions: {
      ...config.plotOptions,
      pie: {
        animation: false,
        allowPointSelect: false,
        showInLegend: true,
        size: customLegend ? '100%' : '70%',
        center: [null, customLegend ? null : '47%'],
        cursor: 'pointer',
        states: {
          hover: {
            brightness: 0,
            halo: { size: 0 },
          },
        },
        dataLabels: {
          enabled: false,
        },
        point: {
          events: {
            legendItemClick() {
              return false;
            },
          },
        },
      },
    },
  };
};

export interface GrowthSimulationConfigProps {
  start: number | undefined;
  end: number | undefined;
  series: SerieValue[];
  theme: Theme;
  frequency: FrequencyEnum;
  print: boolean;
  currencySymbol: string;
  useLogarithmicScale: boolean;
  hasDrawdownLines: boolean;
  axisFontSize: number;
}

export const createGrowthSimulationLineChartConfig = (props: GrowthSimulationConfigProps) => {
  const config = createVennLineChartConfig(
    props.series,
    props.theme,
    props.axisFontSize,
    'allocation',
    props.frequency,
    undefined,
    props.print,
    props.hasDrawdownLines,
    props.currencySymbol,
  );

  return {
    ...config,
    chart: {
      ...config.chart,
      marginRight: 4,
    },
    xAxis: {
      ...config.xAxis,
      showFirstLabel: true,
      startOnTick: false,
      endOnTick: false,
      max: props.end,
    },
    yAxis: merge({}, config.yAxis, {
      startOnTick: false,
      tickPixelInterval: 50,
      type: props.useLogarithmicScale ? 'logarithmic' : ('linear' as AxisTypeValue),
    }),
  };
};

export interface BoxChartConfigProps {
  start: number | undefined;
  end: number | undefined;
  categories: string[];
  theme: Theme;
  frequency: FrequencyEnum;
  print?: boolean;
  currencySymbol: string;
  hasDrawdownLines?: boolean;
  percentiles: number[];
  colors: VennColors;
  useLogarithmicScale?: boolean;
  axisFontSize: number;
}

export const createBoxChartConfig = (props: BoxChartConfigProps) => {
  const config = getDefaultChartConfig(
    'ratio',
    props.frequency,
    props.print ?? false,
    props.theme.Typography,
    props.theme.Colors,
    props.theme.Schemes,
    props.axisFontSize,
    props.currencySymbol,
  );
  return {
    ...config,
    chart: {
      ...config.chart,
      type: 'boxplot',
    },
    xAxis: merge({}, config.xAxis, {
      labels: {
        enabled: false,
      },
    }),
    yAxis: merge({}, config.yAxis, {
      type: props.useLogarithmicScale ? 'logarithmic' : ('linear' as AxisTypeValue),
      labels: {
        formatter(this: AnyDuringEslintMigration) {
          return props.currencySymbol + this.value.toString();
        },
      },
    }),
    tooltip: {
      backgroundColor: getTooltipBackgroundColor(props.colors),
      borderRadius: 10,
      borderWidth: 0,
      valueDecimals: 2,
      shared: false,
      style: {
        color: props.colors.White,
      },
      formatter(this: TooltipFormatterContextObject) {
        const point = this.point as BoxPlotPoint;
        const color = point?.color;

        return `<span style='color:${color}'>\u25CF</span>
      <p><b>${this.series?.name ?? 'Series'}</p></b><br />
      <p>${Numbers.safeFormatPercentage(props.percentiles[4], 0)} :<b> ${
        props.currencySymbol + Numbers.safeFormatNumber(point.options.high)
      } </b></p><br />  
      <p>${Numbers.safeFormatPercentage(props.percentiles[3], 0)} :<b> ${
        props.currencySymbol + Numbers.safeFormatNumber(point.q3)
      } </b></p><br />  
      <p>${Numbers.safeFormatPercentage(props.percentiles[2], 0)} :<b> ${
        props.currencySymbol + Numbers.safeFormatNumber(point.median)
      } </b></p><br />  
      <p>${Numbers.safeFormatPercentage(props.percentiles[1], 0)} :<b> ${
        props.currencySymbol + Numbers.safeFormatNumber(point.q1)
      } </b></p><br />
      <p>${Numbers.safeFormatPercentage(props.percentiles[0], 0)} :<b> ${
        props.currencySymbol + Numbers.safeFormatNumber(point.options.low)
      } </b></p><br />  
      `;
      },
    },
  };
};

/**
 * Scatter plot paddings defined as part of VENN-22406; enabling minimum boundaries around a scatter plot.
 */
const SCATTER_PLOT_PADDING = 0.05;

// Anchor the axis at zero when all data shares a sign — min:0 for non-negative data, max:0 for
// non-positive. The baseline is only a default: a user override on the opposite side that crosses
// zero wins and the baseline yields, so we never emit an inverted axis (min > max).
// The unset side is `null`, not omitted — Highcharts reads `null` as "auto-recalc" but `undefined`
// as "keep previous bound" on update, which would let a stale baseline linger across re-renders.
// Sign is unit-invariant, so comparing `range` (chart units) against 0 is valid.
const resolveZeroBaseline = (values: number[], range: ChartRange): { min: number | null; max: number | null } => {
  if (values.every((v) => v >= 0)) {
    return { min: range.max !== null && range.max < 0 ? null : 0, max: null };
  }

  if (values.every((v) => v <= 0)) {
    return { min: null, max: range.min !== null && range.min > 0 ? null : 0 };
  }

  return { min: null, max: null };
};

// values and range.min/max must be in the same unit space for the comparison to be valid.
// values are chart-space (e.g. 0.05 for 5%); range comes from user input in display-space (e.g. 5 for 5%).
// Convert range bounds to chart-space before comparing.
const computeClips = (
  values: number[],
  range: ScatterAxisRange,
  format: AxisFormat,
): ScatterAxisStaticHints['clipsData'] => {
  const chartMin = !isNil(range.min) ? displayToChart(range.min, format) : null;
  const chartMax = !isNil(range.max) ? displayToChart(range.max, format) : null;
  const below = chartMin !== null && values.some((v) => v < chartMin);
  const above = chartMax !== null && values.some((v) => v > chartMax);

  if (below && above) {
    return 'both';
  }

  if (below) {
    return 'min';
  }

  if (above) {
    return 'max';
  }

  return undefined;
};

const buildRangeMetadata = (
  values: number[],
  range?: ScatterAxisRange,
  dataType?: TabularDataTypeEnum,
): ScatterAxisStaticHints => {
  const format = axisFormatFromDataType(dataType);
  // Mirror the chart's baseline resolution so the axis-config placeholder matches the rendered axis:
  // a user override crossing zero drops the auto baseline on the opposite side.
  const constraint = resolveZeroBaseline(values, toChartRange(range, format));

  return {
    zeroBaseline: constraint.min === 0 ? 'min' : constraint.max === 0 ? 'max' : undefined,
    clipsData: range ? computeClips(values, range, format) : undefined,
    dataType,
  };
};

export const getScatterAxisStaticHints = (
  series: ScatterSeries,
  axisConfig?: ScatterAxisConfig,
  xDataType?: TabularDataTypeEnum,
  yDataType?: TabularDataTypeEnum,
): ScatterAxisStaticMetadata => {
  const xPoints = series.flatMap((s) => s.data.map((p) => p[0]));
  const yPoints = series.flatMap((s) => s.data.map((p) => p[1]));

  return {
    x: buildRangeMetadata(xPoints, axisConfig?.x, xDataType),
    y: buildRangeMetadata(yPoints, axisConfig?.y, yDataType),
  };
};

type ChartRange = {
  min: number | null;
  max: number | null;
  interval: number | null;
};

// Convert a user-supplied axis range from display units (e.g. 5 for 5%) to chart units (0.05).
// Single source of truth — both Highcharts axis overrides and the point-clipping filter use this.
const toChartRange = (range: ScatterAxisRange | undefined, format: AxisFormat): ChartRange => ({
  min: !isNil(range?.min) ? displayToChart(range.min, format) : null,
  max: !isNil(range?.max) ? displayToChart(range.max, format) : null,
  interval: !isNil(range?.interval) ? displayToChart(range.interval, format) : null,
});

// tickInterval must be `null` (not `undefined`) when the user clears a previously-set interval —
// Highcharts treats `undefined` as "keep previous value" on axis update, while `null` forces auto-recalc.
// XAxisOptions.tickInterval is typed `number | undefined`, but `null` is the documented runtime escape
// (https://api.highcharts.com/highcharts/xAxis.tickInterval). Cast localized to this single helper.
const toTickInterval = (interval: number | null): number | undefined => interval as unknown as number | undefined;

// minPadding/maxPadding: 0 prevents Highcharts from adding whitespace beyond the user's explicit bound.
const buildScatterAxisOverrides = (chartRange: ChartRange) => ({
  ...(chartRange.min !== null && { min: chartRange.min, minPadding: 0 }),
  ...(chartRange.max !== null && { max: chartRange.max, maxPadding: 0 }),
  tickInterval: toTickInterval(chartRange.interval),
});

// Generic over the series member so the discriminated union (scatter / scatter3d) stays narrowed
// through the filter. The single `as S` localizes the assertion that filtering preserves the
// element's shape — the data tuple width is unchanged, only points are dropped.
const clipSeriesPoints = <S extends ScatterSeries[number]>(s: S, inRange: (x: number, y: number) => boolean): S =>
  ({ ...s, data: s.data.filter(([x, y]) => inRange(x, y)) }) as S;

const scatterLabelMaxDecimalLength = (positions: readonly number[], format: AxisFormat): number => {
  const scaled = positions.map((p) => (isPercent(format) ? correctFloat(p * 100) : correctFloat(p)));
  return scaled.length ? Math.max(...scaled.map(Numbers.decimalCount)) : 0;
};

const formatScatterLabel = (value: number, format: AxisFormat, maxDecimalLength: number): string =>
  isPercent(format)
    ? `${numberFormat(correctFloat(value * 100), maxDecimalLength)}%`
    : numberFormat(value, maxDecimalLength);

const makeScatterLabelFormatter = (format: AxisFormat) => {
  // tickPositions is identical across every label call within a render and gets a fresh array
  // reference on each render, so cache the derived decimal count keyed by that reference —
  // turns the per-label O(n) scan into O(n) per render instead of O(n²).
  let cachedPositions: readonly number[] | undefined;
  let cachedMaxDecimalLength = 0;

  // Context-free formatting shared by the Highcharts label formatter and the pre-render width
  // estimate, so both render a given tick value identically off one cache.
  const formatValue = (value: number, positions: readonly number[] | undefined): string => {
    if (positions && positions !== cachedPositions) {
      cachedPositions = positions;
      cachedMaxDecimalLength = scatterLabelMaxDecimalLength(positions, format);
    }
    return formatScatterLabel(value, format, cachedMaxDecimalLength);
  };

  return {
    formatValue,
    highchartsFormatter(this: AxisLabelsFormatterContextObject) {
      return formatValue(Number(this.value), this.axis.tickPositions);
    },
  };
};

// Measures a tick label's rendered box by laying out a throwaway SVG text node at the label's font
// and reading its bbox. Runs inside tickPositioner (renderer is ready, before paint) and memoizes per
// value, so each distinct label is measured once per layout. Exact width/height — no estimation.
const makeLabelMeasurer = (
  renderer: SVGRenderer,
  style: CSSObject,
  textFor: (value: number) => string,
): ((value: number) => LabelBox) => {
  const cache = new Map<number, LabelBox>();
  return (value: number): LabelBox => {
    const cached = cache.get(value);
    if (cached) {
      return cached;
    }
    const element = renderer.text(textFor(value)).css(style).add();
    const bbox = element.getBBox();
    element.destroy();
    const box: LabelBox = { width: bbox.width, height: bbox.height };
    cache.set(value, box);
    return box;
  };
};

export const createVennScatterChartConfig = (
  series: ScatterSeries,
  xAxisFormat: AxisFormat,
  yAxisFormat: AxisFormat,
  xTitle: string,
  yTitle: string,
  typography: Typography,
  colors: VennColors,
  schemes: Schemes,
  legendFontSize: number,
  axisFontSize: number,
  height: number | undefined,
  axisConfig?: ScatterAxisConfig,
  onAxisRender?: (extremes: { x: ScatterAxisRenderedExtremes; y: ScatterAxisRenderedExtremes }) => void,
): Highcharts.Options => {
  const xUnit = isPercent(xAxisFormat) ? '%' : '';
  const yUnit = isPercent(yAxisFormat) ? '%' : '';

  const allXValues = series.flatMap((s) => s.data.map((p) => p[0]));
  const allYValues = series.flatMap((s) => s.data.map((p) => p[1]));

  // SoftThreshold (https://api.highcharts.com/highcharts/plotOptions.series.softThreshold) only applies to Y axis for some reason,
  // so we implement our own custom soft threshold behavior for the X axis.
  // This makes the 0 threshold 'soft' such that data can push into it with no minimum padding, but as soon as any data is past the zero threshold
  // we start using padding. This is convenient so that values like 0.01 volatility don't cause the scatter plot to start at a negative number, which would
  // be weird given negatives are impossible with volatility).
  const xSeriesMin = min(allXValues) ?? 0;
  const xAxisPadding = {
    minPadding: xSeriesMin >= 0 ? 0 : SCATTER_PLOT_PADDING,
    // We don't do anything special for maxPadding because surprisingly highcharts handles it well by default
    maxPadding: SCATTER_PLOT_PADDING,
  };
  const yAxisPadding = {
    minPadding: SCATTER_PLOT_PADDING,
    maxPadding: SCATTER_PLOT_PADDING,
  };

  const xChartRange = toChartRange(axisConfig?.x, xAxisFormat);
  const yChartRange = toChartRange(axisConfig?.y, yAxisFormat);

  const xAxisOverrides = buildScatterAxisOverrides(xChartRange);
  const yAxisOverrides = buildScatterAxisOverrides(yChartRange);

  const inRange = (x: number, y: number) =>
    x >= (xChartRange.min ?? -Infinity) &&
    x <= (xChartRange.max ?? Infinity) &&
    y >= (yChartRange.min ?? -Infinity) &&
    y <= (yChartRange.max ?? Infinity);

  // Highcharts auto-fits axes to data even when explicit min/max are set, so points outside
  // the user's range need to be removed before the data reaches the chart.
  const filteredSeries: ScatterSeries = series.map((s) => clipSeriesPoints(s, inRange));

  // One formatter instance per axis, reused for both the Highcharts label formatter and the
  // pre-render width measurement (shares the decimal-count cache between them).
  const xLabelFormatter = makeScatterLabelFormatter(xAxisFormat);
  const yLabelFormatter = makeScatterLabelFormatter(yAxisFormat);
  // Font that drives label width/height; only these two properties affect the measured bbox.
  const labelFontStyle: CSSObject = { fontFamily: typography.fontFamily, fontSize: `${axisFontSize}pt` };
  // Rendered label style for both axes. textOverflow:'none' + whiteSpace:'nowrap' suppress native
  // truncation so rotated labels are never clipped to an ellipsis; the font matches labelFontStyle so
  // the measured bbox reflects the painted label.
  const labelStyle: CSSObject = {
    ...labelFontStyle,
    color: colors.GreyScale.Grey90,
    textOverflow: 'none',
    whiteSpace: 'nowrap',
  };
  // Measures labels for one axis at the shared font, formatting each value off that axis's snapped
  // tick set so the measured text matches what Highcharts will paint.
  const measurerFor = (renderer: SVGRenderer, formatter: typeof xLabelFormatter, snapped: readonly number[]) =>
    makeLabelMeasurer(renderer, labelFontStyle, (value) => formatter.formatValue(value, snapped));

  return {
    chart: {
      type: 'scatter',
      // Label repositioning below reads final label geometry in the render handler; animation would
      // leave labels mid-transition at that point, so disable it to keep measurements accurate.
      animation: false,
      marginRight: 10,
      marginTop: 10,
      style: {
        fontSize: `${axisFontSize}pt`,
        overflow: 'visible',
      },
      spacingBottom: 4,
      height,
      events: {
        // afterSetExtremes fires before the render pass, so tickInterval is not yet computed.
        // render fires after layout, guaranteeing tickInterval is stable.
        render() {
          if (!onAxisRender || !this.xAxis[0] || !this.yAxis[0]) {
            return;
          }

          const xExt = this.xAxis[0].getExtremes();
          const xInterval = this.xAxis[0].tickInterval;

          const yExt = this.yAxis[0].getExtremes();
          const yInterval = this.yAxis[0].tickInterval;

          onAxisRender({
            x: { min: xExt.min, max: xExt.max, interval: xInterval },
            y: { min: yExt.min, max: yExt.max, interval: yInterval },
          });
        },
      },
    },
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    legend: {
      enabled: true,
      useHTML: true,
      itemStyle: {
        fontSize: `${legendFontSize}pt`,
      },
      padding: calculateLegendPadding(legendFontSize),
      margin: 0,
    },
    xAxis: {
      ...xAxisPadding,
      ...resolveZeroBaseline(allXValues, xChartRange),
      ...xAxisOverrides,
      title: {
        text: xTitle,
        align: 'middle' as AxisTitleAlignValue,
      },
      gridLineWidth: 1,
      gridLineColor: schemes.LineChartColors.lineColor,
      tickWidth: 0,
      lineColor: schemes.LineChartColors.lineColor,
      startOnTick: true,
      endOnTick: true,
      tickPixelInterval: 120,
      labels: {
        overflow: 'justify',
        // We own label spacing via dropCrowdedEndTicks + the rotation set in tickPositioner, so never
        // let Highcharts hide a label of its own accord.
        allowOverlap: true,
        // rotation is set per-render by the tickPositioner below (a fixed numeric rotation), which
        // makes Highcharts render the labels natively rotated and skip its slot-derived width cap.
        style: labelStyle,
        formatter: xLabelFormatter.highchartsFormatter,
      },
      // Highcharts computes tick positions before applying min/max, so the outermost ticks may not
      // align with a user-set bound. forceEndTicks snaps them into place; resolveScatterXAxisLabels
      // then drops a neighbour the snap left too close to read and picks the label rotation from the
      // real gaps + measured label sizes.
      tickPositioner() {
        const snapped = forceEndTicks(this.tickPositions ?? [], xAxisOverrides);
        const measure = measurerFor(this.chart.renderer, xLabelFormatter, snapped);
        const { positions, rotation } = resolveScatterXAxisLabels({
          positions: snapped,
          toPixels: (value) => this.toPixels(value, false),
          measure,
        });

        if (this.options.labels) {
          this.options.labels.rotation = rotation;
        }
        // unsquish() derives `labelRotation` in setTickInterval — BEFORE this tickPositioner runs — from
        // the *previous* options.labels.rotation. On a chart.update (axis-config change) the axis is
        // rebuilt, so that previous value is gone and labelRotation lands on 0; renderUnsquish then
        // applies that stale 0 to the labels, overwriting the option we set above, and the rotated label
        // space collapses. Sync labelRotation with the value we just chose (renderUnsquish keeps it,
        // since the longest label easily clears the rotation guard) so offset + applied rotation agree
        // on the reflow and update paths alike.
        (this as unknown as { labelRotation: number }).labelRotation = rotation;

        return positions;
      },
    },
    yAxis: {
      ...yAxisPadding,
      ...resolveZeroBaseline(allYValues, yChartRange),
      ...yAxisOverrides,
      gridLineColor: schemes.LineChartColors.lineColor,
      gridLineWidth: 1,
      tickWidth: 1,
      tickColor: schemes.LineChartColors.lineColor,
      tickLength: 0,
      lineWidth: 1,
      lineColor: schemes.LineChartColors.lineColor,
      startOnTick: true,
      endOnTick: true,
      labels: {
        // We own label spacing via dropCrowdedTicks in the tickPositioner, dropping whole ticks (mark +
        // gridline + label) rather than letting Highcharts hide just the label text and leave orphan
        // marks, so never let Highcharts hide a label of its own accord.
        allowOverlap: true,
        style: labelStyle,
        formatter: yLabelFormatter.highchartsFormatter,
      },
      title: {
        text: yTitle,
        align: 'middle' as AxisTitleAlignValue,
      },
      // Same tick-alignment + crowding rationale as xAxis.tickPositioner above. The y labels stay
      // horizontal, so instead of rotating, dropCrowdedTicks thins the whole axis (interior included)
      // until no two stacked labels overlap.
      tickPositioner() {
        const snapped = forceEndTicks(this.tickPositions ?? [], yAxisOverrides);
        const measure = measurerFor(this.chart.renderer, yLabelFormatter, snapped);
        return dropCrowdedTicks(snapped, (value) => this.toPixels(value, false), measure);
      },
    },
    plotOptions: {
      series: {
        events: {
          legendItemClick: () => false,
        },
      },
      scatter: {
        marker: {
          radius: 5,
          states: {
            hover: {
              enabled: true,
              lineColor: schemes.LineChartColors.lineColor,
            },
          },
        },
      },
    },
    tooltip: {
      backgroundColor: getTooltipBackgroundColor(colors),
      useHTML: true,
      borderRadius: 10,
      borderWidth: 0,
      valueDecimals: 2,
      shared: true,
      style: {
        color: colors.White,
      },
      formatter(this: TooltipFormatterContextObject) {
        const x = isPercent(xAxisFormat) ? (this.x * 100).toFixed(2) + xUnit : this.x.toFixed(2);
        const y = isPercent(yAxisFormat) ? (this.y * 100).toFixed(2) + yUnit : this.y.toFixed(2);
        const color = this.point ? this.point.color : undefined;

        let tooltipStr = `<span style='color:${color}'>\u25CF</span>
        <span> ${this.series?.name ?? 'Series'}</span><br />
        <span>${xTitle}: <b> ${x} </b></span><br />`;

        if (xTitle !== yTitle) {
          tooltipStr += `<span>${yTitle}: <b> ${y} </b></span>`;
        }

        return tooltipStr;
      },
    },
    series: filteredSeries,
  };
};

export const addVenncast = (config: Options, data: VennLineChartData, colors: VennColors, print?: boolean) => ({
  ...config,
  chart: {
    ...config.chart,
    marginBottom: data.persistentBottomBar || data.estimated ? 60 : 30,
    events: {
      ...(config?.chart?.events || {}),
      load(this: Chart) {
        ((config?.chart?.events?.load as () => void) || noop)();
        drawVenncast.bind(this, data, colors, print)();
      },
      redraw(this: Chart) {
        ((config?.chart?.events?.redraw as () => void) || noop)();
        drawVenncast.bind(this, data, colors, print)();
      },
    },
  },
  xAxis: {
    ...config.xAxis,
    plotLines: [
      // TODO: (VENN-20577 / TYPES) this doesn't handle the case where axis is an XAxisOptions[], should it?
      ...((config?.xAxis as XAxisOptions)?.plotLines || []),
      ...(data &&
        (data.estimated && data.visibleVenncastStart !== undefined && !data.persistentBottomBar
          ? [
              {
                color: colors.DataLineColor.PaleBlue,
                value: data.visibleVenncastStart,
                width: data.persistentBottomBar ? 0 : 2,
                zIndex: HighchartZIndex.PlotBand.AboveSeries,
                id: 'venncast-line',
              },
            ]
          : [])),
    ],
  },
});

export function drawVenncast(this: Chart, data: VennLineChartData, colors: VennColors, print?: boolean) {
  const xAxis = this.xAxis[0]!;
  if (!xAxis || !data || (!data.estimated && !data.persistentBottomBar)) {
    return;
  }

  if (Array.isArray(this.xAxisPlotBands)) {
    for (const band of this.xAxisPlotBands) {
      band.safeRemoveChild(band.element);
    }
  }

  this.xAxisPlotBands = [];

  const venncastLine = xAxis.plotLinesAndBands.find((pl) => pl.id === 'venncast-line');
  if (!venncastLine) {
    return;
  }

  const venncastLineBottomY = getXAxisPlotLineBottomY(venncastLine);
  const chartStart = 0;
  const chartEnd = xAxis.width + xAxis.pos;
  const chartWidth = chartEnd - chartStart;

  const greyFill = this.renderer
    .rect(chartStart, venncastLineBottomY + 30, chartWidth, 30)
    .attr({
      id: 'venncast-label-grey-bkg',
      fill: colors.GreyScale.Grey20,
    })
    .add();
  this.xAxisPlotBands.push(greyFill);

  if ((!data.estimated && !data.visibleVenncastStart) || !data.visibleVenncastEnd) {
    return;
  }

  const start = xAxis.toPixels(data.visibleVenncastStart as AnyDuringEslintMigration, false);
  const end = xAxis.toPixels(data.visibleVenncastEnd, false);
  if (start < 0 && end < start) {
    return;
  }

  const highlightWidth = end - start;

  const xAxisPlotBand = this.renderer
    .rect(start, venncastLineBottomY, highlightWidth, 30)
    .attr({
      id: 'vanncast-xaxis-highlight',
      fill: ColorUtils.hex2rgba(colors.DataLineColor.PaleBlue, 0.2),
      zIndex: HighchartZIndex.PlotBand.AboveChart,
    })
    .add();
  this.xAxisPlotBands.push(xAxisPlotBand);

  const blackFill = this.renderer
    .rect(start, venncastLineBottomY + 30, highlightWidth, 30)
    .attr({
      id: 'venncast-label-trailing-black-bkg',
      fill: colors.Black,
    })
    .add();
  this.xAxisPlotBands.push(blackFill);

  const isTrunc = end - start < 200;
  const width = isTrunc ? (end - start - 50 > 0 ? end - start - 50 : 0) : 160;

  // Remove this line if it was previously added, to avoid rendering multiple overlapping tips.
  xAxis.removePlotLine('venncast-tip');
  xAxis.addPlotLine({
    id: 'venncast-tip',
    color: colors.DataLineColor.PaleBlue,
    width: 2,
    value: data.visibleVenncastStart,
    zIndex: HighchartZIndex.PlotBand.AboveChart,
    label: {
      text: ReactDOMServer.renderToString(getVennTip(width, isTrunc, colors)),
      verticalAlign: 'bottom',
      rotation: 0,
      useHTML: true,
      x: 0,
      y: print ? 70 : 72,
      style: {
        fontSize: '11px',
        textOverflow: 'clip',
      },
    },
  });

  if (venncastLine.svgElem.d !== undefined) {
    const dArray = (venncastLine.svgElem.d as AnyDuringEslintMigration).split(' ');
    dArray[5] = Number(dArray[5]) + (data.estimated ? 60 : 30);
    venncastLine.svgElem.d = dArray.join(' ');
    (venncastLine.svgElem.element.attributes as AnyDuringEslintMigration).d.nodeValue = dArray.join(' ');
  }
}

function getXAxisPlotLineBottomY(plotLineStart: PlotLineOrBand) {
  const d = plotLineStart.svgElem.d || '';
  const dArray = (d as AnyDuringEslintMigration).split(' ');
  return Number(dArray[5]);
}

const getVennTip = (width: number, isTrunc: boolean, colors: VennColors) => {
  if (!isTrunc) {
    return (
      <div>
        <StyledVennTip
          href={getHelpCenterLinks().VENNCAST_FAQ_HREF}
          target="_blank"
          width={width}
          className="qa-venntip"
          rel="noopener noreferrer"
          colors={colors}
        >
          <StyledIcon prefix="fas" type="info-circle" />
          <span>{getTextThemeProvider().VenncastName} Estimate Period</span>
        </StyledVennTip>
      </div>
    );
  }

  return (
    <TooltipWrapper>
      <StyledTooltip
        content={<>{getTextThemeProvider().VenncastName} Estimate Period</>}
        position={TooltipPosition.Left}
      >
        <StyledVennTip
          href={getHelpCenterLinks().VENNCAST_FAQ_HREF}
          target="_blank"
          width={width}
          className="qa-venntip"
          rel="noopener noreferrer"
          isTrunc
          colors={colors}
        >
          <StyledIcon prefix="fas" type="info-circle" />
          <span>{getTextThemeProvider().VenncastName} Estimate Period</span>
        </StyledVennTip>
      </StyledTooltip>
    </TooltipWrapper>
  );
};

const TooltipWrapper = styled.div`
  position: relative;
  height: 30px;
  margin-top: -13px;
`;

const StyledVennTip = styled.a<{ width?: number; isTrunc?: boolean; colors: VennColors }>`
  padding: 6px 9px;
  background-color: ${(props) => props.colors.DataLineColor.PaleBlue};
  border-right: 0.5px solid ${(props) => props.colors.DataLineColor.PaleBlue};
  color: ${(props) => props.colors.Black};

  &:hover {
    color: ${(props) => props.colors.White};
  }

  font-weight: bold;
  font-size: 12px;
  position: absolute;
  bottom: 0;
  left: 0;

  :after {
    top: 0;
    border-width: 15px;
    border-color: ${(props) => props.colors.DataLineColor.PaleBlue} transparent transparent transparent;
    transform: rotate(-90deg);
    position: absolute;
    left: calc(100% + 0.5px);
    border-style: solid;
    content: '';
  }

  span {
    width: ${(props) => (props.width ? props.width : '140')}px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    display: inline-block;
  }
`;

const StyledIcon = styled(Icon)`
  padding-right: 5px;
  position: relative;
  top: -2px;
`;

const StyledTooltip = styled(Tooltip)`
  > div {
    top: -15px;
    height: 30px;
    right: calc(8px + 100%);
  }

  > a {
    left: -1px;
    bottom: -2px;
  }
`;

export const toPercentage = (value: number) => round(value * 100, 12);

export const getLineSerie = ({
  data,
  name,
  color,
  type,
  lastReturn,
  zIndex,
  createForEmptyReturns = false,
  marker,
}: {
  data: number[][] | undefined;
  name: string;
  color: string;
  type?: HighchartChartType;
  lastReturn?: number[];
  zIndex?: number;
  createForEmptyReturns?: boolean;
  marker?: PartialHighchartSerieData['marker'];
}): PartialHighchartSerieData | undefined => {
  if ((!data || data.length === 0) && !createForEmptyReturns) {
    return undefined;
  }
  const format = (value: number) => round(value, 12);
  if (type === 'arearange') {
    let estimatedBounds = (data || []).map((d) => [d[0]!, format(d[1]!), format(d[2]!)] as [number, number, number]);
    if (lastReturn) {
      estimatedBounds = [[lastReturn[0]!, format(lastReturn[1]!), format(lastReturn[1]!)], ...estimatedBounds];
    }
    return {
      name,
      type: 'arearange',
      zIndex,
      color,
      data: estimatedBounds,
      fillColor: ColorUtils.hex2rgba(color, 0.1),
      lineColor: color,
      lineWidth: 0.3,
      ...(!isNil(marker) ? { marker } : {}),
    };
  }
  if (type === 'dashline') {
    let estimatedReturn = (data || []).map((d) => [d[0]!, format(d[1]!)] as [number, number]);
    if (lastReturn) {
      estimatedReturn = [[lastReturn[0]!, format(lastReturn[1]!)], ...estimatedReturn];
    }
    return {
      name,
      type: 'line',
      zIndex,
      color,
      data: estimatedReturn,
      dashStyle: 'ShortDot',
      ...(!isNil(marker) ? { marker } : {}),
    };
  }
  const formattedData = (data || []).map((d) => [d[0]!, format(d[1]!)] as [number, number]);
  if (type === 'scatter') {
    return {
      name,
      type,
      zIndex,
      color,
      data: formattedData,
      marker: {
        ...marker,
        enabled: true,
      },
    };
  }
  return {
    name,
    type: type ?? 'line',
    zIndex,
    color,
    data: formattedData,
    ...(!isNil(marker) ? { marker } : {}),
  };
};
